import { Injectable } from '@angular/core';
import * as mapboxgl from 'mapbox-gl';
import type { Feature, FeatureCollection, Point } from 'geojson';
import { IMarker, IMarkerCoordinate, IMarkerOptions } from '../models/marker.interface';
import {
    PIN_REFERENCE_ZOOM,
    parseMarkerCssPx,
    pinIconSizeMapboxExpression,
} from './map-pin-layer.util';

const PIN_IMAGE_ID = 'cropmap-pin-location';
const PIN_IMAGE_URL = '/images/pin-location.svg';
const PIN_IMAGE_PIXEL_RATIO = 2;
export const PIN_ACTIVE_SOURCE = 'cropmap-pin-active-source';
export const PIN_ACTIVE_LAYER = 'cropmap-pin-active-layer';
export const PINS_BATCH_SOURCE = 'cropmap-pins-batch-source';
export const PINS_BATCH_LAYER = 'cropmap-pins-batch-layer';

/** Default screen-pixel offset for legacy DOM markers ({@link mapboxgl.Marker} offset). */
const DEFAULT_PIN_OFFSET: [number, number] = [0, 40];

/**
 * `pin-location.svg` (60×65) draws its tip at about (26.4, 54); the rest is drop-shadow padding.
 * With `icon-anchor: bottom`, this `icon-offset` (icon px, scaled by `icon-size`) puts the tip on the point.
 */
const PIN_TIP_ICON_OFFSET: [number, number] = [3.6, 11];

const EMPTY_PIN_COLLECTION: FeatureCollection<Point> = { type: 'FeatureCollection', features: [] };

export interface MapPinLayerContext {
    getMap: () => mapboxgl.Map | undefined;
    getBeforeLabelLayerId: () => string | undefined;
}

@Injectable({ providedIn: 'root' })
export class MapPinLayerService {
    private context: MapPinLayerContext | null = null;

    private pinImageLoadPromise: Promise<void> | null = null;
    private pinImageWidthPx = 64;
    private pinScale = { basePx: 20, refZoom: PIN_REFERENCE_ZOOM, minPx: 20, maxPx: 80, growthPerZoom: 2 };
    private pinIconTranslate: [number, number] = [0, 0];
    private pinDomMarker: mapboxgl.Marker | undefined;
    private batchDomMarkers: mapboxgl.Marker[] = [];
    private batchPinHandlersRegistered = false;
    private batchPinMouseEnter: ((e: mapboxgl.MapLayerMouseEvent) => void) | undefined;
    private batchPinMouseLeave: (() => void) | undefined;

    private readonly markerPopup = new mapboxgl.Popup({
        offset: 25,
        closeButton: false,
        closeOnClick: false,
    });

    /** Wire map accessors from {@link MapService} when the map is built or torn down. */
    configure(context: MapPinLayerContext): void {
        this.context = context;
    }

    /** Clears cached load state and map wiring after {@link MapService} destroys the map. */
    reset(): void {
        this.pinImageLoadPromise = null;
        this.pinDomMarker = undefined;
        this.batchDomMarkers = [];
        this.batchPinHandlersRegistered = false;
        this.batchPinMouseEnter = undefined;
        this.batchPinMouseLeave = undefined;
        this.context = null;
    }

    /**
     * Places or replaces the single “pinned” marker (does not touch batch markers).
     * With {@link IMarkerOptions.scaleWithZoom}, uses a Mapbox symbol layer (map-native scaling).
     */
    addPinMarker(coordinate: IMarkerCoordinate, options?: IMarkerOptions): mapboxgl.Marker {
        this.removePinMarker();
        const map = this.context?.getMap();
        if (!map) {
            return {} as mapboxgl.Marker;
        }

        if (options?.scaleWithZoom) {
            this.applyPinOptions(options);
            void this.ensurePinImageLoaded()
                .then(() => {
                    const currentMap = this.context?.getMap();
                    if (!currentMap) {
                        return;
                    }
                    const feature = this.buildPinPointFeature(coordinate);
                    this.ensurePinSymbolLayer(PIN_ACTIVE_SOURCE, PIN_ACTIVE_LAYER);
                    const source = currentMap.getSource(PIN_ACTIVE_SOURCE) as mapboxgl.GeoJSONSource;
                    source.setData({ type: 'FeatureCollection', features: [feature] });
                })
                .catch((err) => console.error('[MapPinLayerService] pin image load failed', err));
            return {} as mapboxgl.Marker;
        }

        this.pinDomMarker = this.createDomMarkerElement(map, coordinate, options);
        return this.pinDomMarker;
    }

    /** Clears only the single pin marker. */
    removePinMarker(): void {
        this.setPinSourceData(PIN_ACTIVE_SOURCE, EMPTY_PIN_COLLECTION);
        this.pinDomMarker?.remove();
        this.pinDomMarker = undefined;
    }

    addMultipleMarkers(markersData: IMarker[]): void {
        const map = this.context?.getMap();
        if (!map || markersData.length === 0) {
            return;
        }

        const useSymbol = markersData.every((m) => m.options?.scaleWithZoom);
        if (!useSymbol) {
            for (const item of markersData) {
                this.batchDomMarkers.push(this.createDomMarkerElement(map, item.coordinate, item.options));
            }
            return;
        }

        this.applyPinOptions(markersData[0].options);
        void this.ensurePinImageLoaded()
            .then(() => {
                const currentMap = this.context?.getMap();
                if (!currentMap) {
                    return;
                }
                const features = markersData.map((item) =>
                    this.buildPinPointFeature(item.coordinate, item.options.popup?.html),
                );
                this.ensurePinSymbolLayer(PINS_BATCH_SOURCE, PINS_BATCH_LAYER);
                this.registerBatchPinHoverHandlers();
                const source = currentMap.getSource(PINS_BATCH_SOURCE) as mapboxgl.GeoJSONSource;
                source.setData({ type: 'FeatureCollection', features });
            })
            .catch((err) => console.error('[MapPinLayerService] pin image load failed', err));
    }

    /** Clears batch observation markers only; keeps the single pin marker if present. */
    removeBatchMarkers(): void {
        this.detachBatchPinHoverHandlers();
        this.setPinSourceData(PINS_BATCH_SOURCE, EMPTY_PIN_COLLECTION);
        for (const m of this.batchDomMarkers) {
            m.remove();
        }
        this.batchDomMarkers = [];
        this.markerPopup.remove();
    }

    removeAllMarkers(): void {
        this.removePinMarker();
        this.removeBatchMarkers();
    }

    /** Keeps observation pin symbols above field/district fills and below basemap labels. */
    ensureLayersAboveFields(): void {
        const map = this.context?.getMap();
        if (!map) {
            return;
        }
        const beforeId = this.context?.getBeforeLabelLayerId();
        for (const layerId of [PIN_ACTIVE_LAYER, PINS_BATCH_LAYER]) {
            if (map.getLayer(layerId)) {
                map.moveLayer(layerId, beforeId);
            }
        }
    }

    private ensurePinImageLoaded(): Promise<void> {
        const map = this.context?.getMap();
        if (!map) {
            return Promise.resolve();
        }
        // Map lost the pin image (e.g. new style) — forget the old load so we register it again.
        if (!map.hasImage(PIN_IMAGE_ID)) {
            this.pinImageLoadPromise = null;
        }
        // Pin image is already loading — wait for that instead of loading twice.
        if (this.pinImageLoadPromise) {
            return this.pinImageLoadPromise;
        }

        this.pinImageLoadPromise = this.loadPinImageData()
            .then(({ data, width }) => {
                const currentMap = this.context?.getMap();
                if (!currentMap) {
                    return;
                }
                // Register rasterized SVG once so symbol layers can use icon-image: cropmap-pin-location.
                if (!currentMap.hasImage(PIN_IMAGE_ID)) {
                    currentMap.addImage(PIN_IMAGE_ID, data, { pixelRatio: PIN_IMAGE_PIXEL_RATIO });
                }
                // Logical width (60) — used to convert px sizes to Mapbox icon-size em units.
                this.pinImageWidthPx = width > 0 ? width : 64;
            })
            .catch((err) => {
                // Allow a later pin add to retry after a failed load.
                this.pinImageLoadPromise = null;
                throw err;
            });
        return this.pinImageLoadPromise;
    }

    /** Rasterize SVG pin asset for Mapbox (loadImage cannot decode SVG). */
    private loadPinImageData(): Promise<{ data: ImageData; width: number }> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                try {
                    const width = img.naturalWidth > 0 ? img.naturalWidth : 60;
                    const height = img.naturalHeight > 0 ? img.naturalHeight : 65;
                    const canvas = document.createElement('canvas');
                    canvas.width = width * PIN_IMAGE_PIXEL_RATIO;
                    canvas.height = height * PIN_IMAGE_PIXEL_RATIO;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        reject(new Error('Canvas 2d unavailable'));
                        return;
                    }
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    resolve({ data: ctx.getImageData(0, 0, canvas.width, canvas.height), width });
                } catch (err) {
                    reject(err);
                }
            };
            img.onerror = () => reject(new Error(`Failed to load pin image: ${PIN_IMAGE_URL}`));
            img.src = PIN_IMAGE_URL;
        });
    }

    /** Shared layout for active + batch symbol pins (scale, screen offset). */
    private applyPinOptions(options: IMarkerOptions): void {
        const scale = options.scaleWithZoom;
        if (scale) {
            this.pinScale = {
                basePx: parseMarkerCssPx(options.width ?? '40px'),
                refZoom: scale.refZoom ?? PIN_REFERENCE_ZOOM,
                minPx: scale.minPx,
                maxPx: scale.maxPx,
                growthPerZoom: scale.growthPerZoom ?? 2,
            };
        }
        this.pinIconTranslate = options.offset ?? [0, 0];
    }

    private syncPinSymbolLayout(map: mapboxgl.Map, layerId: string): void {
        map.setLayoutProperty(layerId, 'icon-size', this.pinIconSizeExpression());
        map.setPaintProperty(layerId, 'icon-translate', this.pinIconTranslate);
    }

    /** Layer-level `icon-size` in em from {@link pinScale} and loaded pin image logical width. */
    private pinIconSizeExpression(): mapboxgl.ExpressionSpecification {
        const w = this.pinImageWidthPx > 0 ? this.pinImageWidthPx : 64;
        const { basePx, refZoom, minPx, maxPx, growthPerZoom } = this.pinScale;
        return pinIconSizeMapboxExpression(basePx / w, refZoom, minPx / w, maxPx / w, growthPerZoom);
    }

    /**
     * GeoJSON point for a pin source. Size/zoom live on the symbol layer, not feature properties,
     * so viewport refreshes do not reset scale. Batch pins may include trusted `popupHtml` for hover.
     */
    private buildPinPointFeature(coordinate: IMarkerCoordinate, popupHtml?: string): Feature<Point> {
        const properties: Record<string, string> = {};
        if (popupHtml) {
            properties['popupHtml'] = popupHtml;
        }
        return {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [coordinate.lng, coordinate.lat] },
            properties,
        };
    }

    /**
     * Creates or updates a symbol layer on `sourceId` (active pin vs batch). Inserts before basemap labels,
     * then {@link ensureLayersAboveFields} so field fills do not cover pins after tile reloads.
     */
    private ensurePinSymbolLayer(sourceId: string, layerId: string): void {
        const map = this.context?.getMap();
        if (!map) {
            return;
        }

        if (!map.getSource(sourceId)) {
            map.addSource(sourceId, { type: 'geojson', data: EMPTY_PIN_COLLECTION });
        }

        const iconSize = this.pinIconSizeExpression();
        const beforeId = this.context?.getBeforeLabelLayerId();

        if (map.getLayer(layerId)) {
            this.syncPinSymbolLayout(map, layerId);
            this.ensureLayersAboveFields();
            return;
        }

        map.addLayer(
            {
                id: layerId,
                type: 'symbol',
                source: sourceId,
                layout: {
                    'icon-image': PIN_IMAGE_ID,
                    'icon-anchor': 'bottom',
                    'icon-offset': PIN_TIP_ICON_OFFSET,
                    'icon-allow-overlap': true,
                    'icon-size': iconSize,
                },
                paint: {
                    'icon-translate': this.pinIconTranslate,
                },
            },
            beforeId,
        );
        this.ensureLayersAboveFields();
    }

    /** Replaces all points in a pin GeoJSON source (empty collection = clear markers). */
    private setPinSourceData(sourceId: string, data: FeatureCollection<Point>): void {
        const map = this.context?.getMap();
        if (!map) {
            return;
        }
        const source = map.getSource(sourceId);
        if (source && 'setData' in source) {
            (source as mapboxgl.GeoJSONSource).setData(data);
        }
    }

    /** One-time layer `mouseenter` / `mouseleave` on {@link PINS_BATCH_LAYER} → {@link markerPopup}. */
    private registerBatchPinHoverHandlers(): void {
        const map = this.context?.getMap();
        if (!map || this.batchPinHandlersRegistered) {
            return;
        }

        this.batchPinMouseEnter = (e: mapboxgl.MapLayerMouseEvent) => {
            const props = e.features?.[0]?.properties as { popupHtml?: string } | undefined;
            const html = props?.popupHtml;
            if (typeof html === 'string' && html.length > 0) {
                map.getCanvas().style.cursor = 'pointer';
                this.markerPopup.setLngLat(e.lngLat).setHTML(html).addTo(map);
            }
        };
        this.batchPinMouseLeave = () => {
            map.getCanvas().style.cursor = '';
            this.markerPopup.remove();
        };

        map.on('mouseenter', PINS_BATCH_LAYER, this.batchPinMouseEnter);
        map.on('mouseleave', PINS_BATCH_LAYER, this.batchPinMouseLeave);
        this.batchPinHandlersRegistered = true;
    }

    /** Removes batch hover listeners (clear batch markers or map destroy). */
    private detachBatchPinHoverHandlers(): void {
        const map = this.context?.getMap();
        if (map && this.batchPinHandlersRegistered && this.batchPinMouseEnter && this.batchPinMouseLeave) {
            map.off('mouseenter', PINS_BATCH_LAYER, this.batchPinMouseEnter);
            map.off('mouseleave', PINS_BATCH_LAYER, this.batchPinMouseLeave);
        }
        this.batchPinHandlersRegistered = false;
        this.batchPinMouseEnter = undefined;
        this.batchPinMouseLeave = undefined;
    }

    /** Legacy HTML overlay marker (no scaleWithZoom). */
    private createDomMarkerElement(
        map: mapboxgl.Map,
        coordinate: IMarkerCoordinate,
        options?: IMarkerOptions,
    ): mapboxgl.Marker {
        const backgroundImage =
            options?.backgroundImage ?? `url(https://www.cropmap.com/assets/images/marker.png)`;
        const width = options?.width ?? `24px`;
        const height = options?.height ?? `30px`;

        const el = document.createElement('div');
        el.style.backgroundImage = backgroundImage;
        el.style.width = width;
        el.style.height = height;
        el.style.backgroundSize = '100%';

        const markerOptions: mapboxgl.MarkerOptions = {
            offset: options?.offset ?? DEFAULT_PIN_OFFSET,
        };
        if (options?.anchor) {
            markerOptions.anchor = options.anchor;
        }

        const marker = new mapboxgl.Marker(el, markerOptions)
            .setLngLat([coordinate.lng, coordinate.lat])
            .addTo(map);

        if (options?.popup?.html) {
            const html = options.popup.html;
            el.addEventListener('mouseenter', () => {
                this.markerPopup.setLngLat(marker.getLngLat()).setHTML(html).addTo(map);
            });
        }

        return marker;
    }
}
